import { Link, useNavigate } from "react-router";
import {
  BID_MIN_WORDS,
  BIDS_REQUIRED,
  COMPETITIVE_MIN,
  LEARNING_BIDS,
  type JobFilter,
  type JobTile,
  type PitchView,
} from "../../api/pitch";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { inputShell } from "../../components/Field";
import { GateFooter } from "../../components/GateFooter";
import { Guide } from "../../components/Guide";
import { ScreenHeader } from "../../components/ScreenHeader";
import { StepChecklist } from "../../components/StepChecklist";

interface JobBoardScreenProps {
  trackLabel: string;
  pitch: PitchView;
  filter: JobFilter;
  tiles: JobTile[];
  total: number;
  loadingJobs: boolean;
  busy: boolean;
  onFilter: (patch: Partial<JobFilter>) => void;
  onLoadMore: () => void;
  onComplete: () => void;
}

function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: [T, string][];
  onChange: (value: T) => void;
}) {
  return (
    <select
      aria-label={label}
      className="min-h-9 rounded-[9px] border-[1.5px] border-border bg-surface px-2.5 text-[13px]"
      value={value}
      onChange={(event) => onChange(event.target.value as T)}
    >
      {options.map(([v, t]) => (
        <option key={v} value={v}>
          {t}
        </option>
      ))}
    </select>
  );
}

function ScoreCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: React.ReactNode;
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <small className="text-[11px] font-bold uppercase tracking-[0.08em] text-text-muted">
        {label}
      </small>
      <div className="font-[family-name:var(--font-display)] text-[26px] font-extrabold text-accent tabular">
        {value}
      </div>
      <div className="text-[12.5px] text-text-muted">{detail}</div>
    </div>
  );
}

/** Step 3 (v2): the practice job board. 10 counted bids complete it; there is no shortlist or agreement. */
export function JobBoardScreen(props: JobBoardScreenProps) {
  const { pitch, filter } = props;
  const navigate = useNavigate();
  const counted = pitch.bidsCounted;
  return (
    <div>
      <ScreenHeader
        kicker="Step 3 · Pitch"
        title="Practice winning work before real money is on the line."
        sub={`Browse realistic ${props.trackLabel} jobs and write proposals that could win them. Every bid is scored the way a client reads it in the first few seconds. Win ${BIDS_REQUIRED} to complete this step.`}
      />
      <Guide guideKey="gig3" done={pitch.done} />
      <StepChecklist gate={pitch.gate} done={pitch.done} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ScoreCard
          label="Bids counted"
          value={`${counted}/${BIDS_REQUIRED}`}
          detail={
            counted >= BIDS_REQUIRED
              ? "Done: you can complete Step 3"
              : `${BIDS_REQUIRED - counted} more to go`
          }
        />
        <ScoreCard
          label="Readiness score"
          value={pitch.readiness != null ? `${pitch.readiness}%` : "–"}
          detail="The average of your counted bids"
        />
      </div>
      <Banner tone="note">
        <b>How bids count:</b> your first {LEARNING_BIDS} bids come with a model proposal to learn
        from. From bid {LEARNING_BIDS + 1}, a bid counts only when it's competitive: at least{" "}
        {BID_MIN_WORDS} words, in your own words, and scoring at least {COMPETITIVE_MIN} of 7,
        including being personalized, answering the client's need and leading with proof. One-liners
        and copy-paste never count.
      </Banner>
      <input
        type="search"
        aria-label="Search jobs"
        placeholder="Search jobs by title or keyword"
        className={`${inputShell} mb-3`}
        value={filter.search ?? ""}
        onChange={(event) => props.onFilter({ search: event.target.value })}
      />
      <div className="mb-2 flex flex-wrap gap-2">
        <Select
          label="Show"
          value={filter.show}
          onChange={(show) => props.onFilter({ show })}
          options={[
            ["all", "Show: all jobs"],
            ["bid", "Show: jobs I bid on"],
          ]}
        />
        <Select
          label="Budget"
          value={filter.budget}
          onChange={(budget) => props.onFilter({ budget })}
          options={[
            ["any", "Any budget"],
            ["lt200", "Under $200"],
            ["b200to500", "$200 to $500"],
            ["b500to1000", "$500 to $1000"],
            ["gt1000", "$1000+"],
          ]}
        />
        <Select
          label="Posted"
          value={filter.posted}
          onChange={(posted) => props.onFilter({ posted })}
          options={[
            ["any", "Any time posted"],
            ["h24", "Last 24 hours"],
            ["d3", "Last 3 days"],
            ["d7", "Last week"],
          ]}
        />
        <Select
          label="Urgency"
          value={filter.urgentOnly ? "urgent" : "any"}
          onChange={(value) => props.onFilter({ urgentOnly: value === "urgent" })}
          options={[
            ["any", "Any urgency"],
            ["urgent", "Urgent only"],
          ]}
        />
        <Select
          label="Sort"
          value={filter.sort}
          onChange={(sort) => props.onFilter({ sort })}
          options={[
            ["newest", "Sort: newest"],
            ["budgetDesc", "Sort: budget, high to low"],
            ["budgetAsc", "Sort: budget, low to high"],
            ["fewest", "Sort: fewest proposals"],
          ]}
        />
      </div>
      <p className="text-[13px] text-text-muted">
        {props.total} job{props.total === 1 ? "" : "s"} match
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {props.tiles.map(({ job, bid }) => {
          const to = `/gig/3/jobs/${job.code}`;
          /*
           * Three looks for three situations: solid crimson to start a bid, red outline when a bid needs fixing, green
           * outline when it already counts. One glance across the board shows what is left to do.
           */
          const action = !bid
            ? {
                label: "Write a bid →",
                className: "border-accent bg-accent text-on-accent hover:bg-accent-pressed",
              }
            : bid.counts
              ? {
                  label: "✓ View my bid",
                  className: "border-positive bg-positive-tint text-positive hover:bg-surface",
                }
              : {
                  label: "✎ Fix my bid",
                  className: "border-negative bg-surface text-negative hover:bg-negative-tint",
                };
          return (
            /*
             * The whole card opens the job (the mock's tiles were clickable, and only the small title was here, which
             * read as plain text). The inner link stops the click so it doesn't navigate twice.
             */
            <div
              key={job.code}
              role="link"
              tabIndex={-1}
              onClick={() => void navigate(to)}
              className={`flex cursor-pointer flex-col gap-1.5 rounded-[var(--radius-card)] border bg-surface p-4 transition-colors hover:border-accent ${
                bid?.counts ? "border-positive" : "border-border"
              }`}
            >
              <div className="flex justify-between text-[11.5px] text-text-muted">
                <span>{job.loc}</span>
                {job.urgent ? <span className="font-bold text-kicker">Urgent</span> : null}
              </div>
              <span className="text-[15px] font-bold text-text">{job.title}</span>
              <div className="text-[13px] text-text-muted">{job.client}</div>
              <div className="font-bold text-accent tabular">
                ${job.lo}–${job.hi}
              </div>
              <div className="flex justify-between text-[12px] text-text-muted">
                <span>
                  {job.posted} · {job.proposals} proposals
                </span>
                {bid ? (
                  <span
                    className={
                      bid.counts ? "font-semibold text-positive" : "font-semibold text-negative"
                    }
                  >
                    {bid.counts ? `✓ Counted · ${bid.pct}%` : `Not counted yet · ${bid.pct}%`}
                  </span>
                ) : null}
              </div>
              <div className="mt-1 flex flex-wrap gap-2">
                <Link
                  to={to}
                  onClick={(event) => event.stopPropagation()}
                  className={`inline-flex min-h-9 items-center rounded-[9px] border-[1.5px] px-4 text-[13px] font-bold ${action.className}`}
                >
                  {action.label}
                </Link>
              </div>
            </div>
          );
        })}
      </div>
      {!props.tiles.length && !props.loadingJobs ? (
        <p className="text-[13px]">No jobs match these filters.</p>
      ) : null}
      {props.tiles.length < props.total ? (
        <div className="mt-3">
          <Button variant="ghost" disabled={props.loadingJobs} onClick={props.onLoadMore}>
            {props.loadingJobs ? "Loading..." : "Load more jobs"}
          </Button>
        </div>
      ) : null}
      <GateFooter
        gate={pitch.gate}
        done={pitch.done}
        label="Complete Step 3"
        next={{ to: "/gig/4", label: "Continue to Step 4" }}
        busy={props.busy}
        onComplete={props.onComplete}
      />
    </div>
  );
}
